class UnifiedCollectionPage {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-unified-grid]');
    this.gridSection = root.querySelector('.uc-grid-section');
    this.facets = root.querySelector('[data-uc-facets]');
    this.facetsForm = root.querySelector('[data-facets-form]');
    this.loadMoreBtn = root.querySelector('[data-load-more]');
    this.klarnaToggle = root.querySelector('[data-klarna-toggle]');
    this.typeBtns = [...root.querySelectorAll('.CollectionTypeBtn')];
    this.currentType = 'opticals';
    this.filterDebounceTimer = null;
    this.priceInputDebounceTimer = null;
    this.isFiltering = false;
    this.defaultLoadingLabel = 'Applying filters...';

    this.bindFacets();
    this.bindFacetsAjax();
    this.bindPriceRangeSelectors();
    this.bindClearAll();
    this.syncSortByFromUrl();
    this.bindLoadMore();
    this.bindKlarnaToggle();
    this.bindTypeToggle();
    this.bindHeroVideo();
    this.syncKlaviyoTeaserOffset = this.syncKlaviyoTeaserOffset.bind(this);
    window.addEventListener('resize', this.syncKlaviyoTeaserOffset);

    if (this.root.dataset.keepFacetsOpen === 'true') {
      this.setFacetsOpen(true);
      this.root.dataset.keepFacetsOpen = 'false';
    } else {
      this.syncScrollLock();
    }
  }

  bindFacets() {
    const facetsRoot = this.root.querySelector('[data-uc-facets]');
    if (!facetsRoot) return;

    const openTrigger = this.root.querySelector('[data-open-facets]');
    const overlay = facetsRoot.querySelector('[data-facets-overlay]');
    const closeBtn = facetsRoot.querySelector('[data-facets-close]');

    const open = () => this.setFacetsOpen(true);
    const close = () => this.setFacetsOpen(false);

    openTrigger?.addEventListener('click', open);
    overlay?.addEventListener('click', close);
    closeBtn?.addEventListener('click', close);
  }

  bindFacetsAjax() {
    if (!this.facetsForm) return;

    this.facetsForm.addEventListener('submit', (event) => {
      event.preventDefault();
      if (this.isFiltering) return;
      window.clearTimeout(this.filterDebounceTimer);
      window.clearTimeout(this.priceInputDebounceTimer);
      this.filterDebounceTimer = null;
      this.priceInputDebounceTimer = null;
      this.applyFacetsAjax({ keepDrawerOpen: false });
      this.setFacetsOpen(false);
    });

    this.facetsForm.addEventListener('change', (event) => {
      if (this.isFiltering) return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.type === 'number' &&
        target.name &&
        target.name.startsWith('filter.')
      ) {
        // Price inputs use a longer input debounce; avoid duplicate requests on change.
        return;
      }
      window.clearTimeout(this.filterDebounceTimer);
      window.clearTimeout(this.priceInputDebounceTimer);
      this.priceInputDebounceTimer = null;
      this.filterDebounceTimer = window.setTimeout(() => {
        this.applyFacetsAjax({ keepDrawerOpen: true });
      }, 120);
    });

    this.facetsForm.addEventListener('input', (event) => {
      if (this.isFiltering) return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.type === 'range' &&
        target.dataset.priceRangeSlider !== undefined
      ) {
        window.clearTimeout(this.filterDebounceTimer);
        this.filterDebounceTimer = window.setTimeout(() => {
          this.applyFacetsAjax({ keepDrawerOpen: true });
        }, 180);
        return;
      }
      if (
        !(target instanceof HTMLInputElement) ||
        target.type !== 'number' ||
        !target.name ||
        !target.name.startsWith('filter.')
      ) {
        return;
      }

      // Do not fire per keystroke for price; wait until typing pauses.
      window.clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
      window.clearTimeout(this.priceInputDebounceTimer);
      this.priceInputDebounceTimer = window.setTimeout(() => {
        this.applyFacetsAjax({ keepDrawerOpen: true });
      }, 700);
    });
  }

  bindPriceRangeSelectors() {
    if (!this.facetsForm) return;
    const groups = [...this.facetsForm.querySelectorAll('[data-price-range-group]')];
    if (!groups.length) return;

    groups.forEach((group) => {
      const minSlider = group.querySelector('[data-price-range-min-slider]');
      const minInput = group.querySelector('[data-price-range-min-input]');
      const maxInput = group.querySelector('[data-price-range-max-input]');
      const minLabel = group.querySelector('[data-price-range-min-label]');
      const maxLabel = group.querySelector('[data-price-range-max-label]');
      if (!minSlider || !minInput || !maxInput || !minLabel || !maxLabel) return;

      const minDefault = Number.parseFloat(group.dataset.minDefault || '0') || 0;
      const maxDefault = Number.parseFloat(group.dataset.maxDefault || '0') || 0;
      const clamp = (value) => {
        if (!Number.isFinite(value)) return minDefault;
        return Math.min(maxDefault, Math.max(minDefault, value));
      };

      const formatValue = (value) => {
        const rounded = Math.round(value * 100) / 100;
        return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/\.?0+$/, '');
      };

      const sync = () => {
        const minValue = clamp(Number.parseFloat(minSlider.value));
        minSlider.value = String(minValue);
        minLabel.textContent = formatValue(minValue);
        maxLabel.textContent = formatValue(maxDefault);

        minInput.value = minValue > minDefault ? formatValue(minValue) : '';
        // Keep max open-ended at collection max.
        maxInput.value = '';
      };

      minSlider.addEventListener('input', sync);
      minSlider.addEventListener('change', sync);
      sync();
    });
  }

  syncSortByFromUrl() {
    if (!this.facetsForm) return;
    const urlParams = new URLSearchParams(window.location.search || '');
    const sortBy = urlParams.get('sort_by');
    const radios = [...this.facetsForm.querySelectorAll('input[type="radio"][name="sort_by"]')];
    if (!radios.length) return;

    if (!sortBy) {
      radios.forEach((r) => (r.checked = false));
      return;
    }

    let matched = false;
    radios.forEach((r) => {
      const isMatch = r.value === sortBy;
      r.checked = isMatch;
      if (isMatch) matched = true;
    });

    if (!matched) {
      radios.forEach((r) => (r.checked = false));
    }
  }

  bindClearAll() {
    const clearLinks = [...this.root.querySelectorAll('[data-clear-all]')];
    if (!clearLinks.length) return;

    clearLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.isFiltering) return;

        const hasActive =
          Boolean(this.facetsForm?.querySelector('input[type="checkbox"]:checked')) ||
          Boolean(this.facetsForm?.querySelector('input[type="radio"][name="sort_by"]:checked')) ||
          Boolean(this.facetsForm?.querySelector('input[type="number"][name^="filter."][value]:not([value=""])')) ||
          Boolean(this.facetsForm?.querySelector('input[type="hidden"][name^="filter.v.price"][value]:not([value=""])'));

        // If nothing is selected, do nothing (prevents refresh).
        if (!hasActive) {
          this.setFacetsOpen(false);
          return;
        }

        if (!this.facetsForm) return;

        // Reset all filter inputs AND sort_by.
        this.facetsForm.querySelectorAll('input').forEach((input) => {
          if (input.type === 'checkbox') input.checked = false;
          if (input.type === 'radio' && input.name === 'sort_by') input.checked = false;
          if (input.type === 'number') input.value = '';
          if (input.type === 'hidden' && input.name && input.name.startsWith('filter.v.price')) input.value = '';
          if (input.type === 'range' && input.dataset.priceRangeSlider !== undefined) {
            input.value = input.min || '0';
          }
        });

        window.clearTimeout(this.filterDebounceTimer);
        window.clearTimeout(this.priceInputDebounceTimer);
        this.filterDebounceTimer = null;
        this.priceInputDebounceTimer = null;
        this.applyFacetsAjax({ keepDrawerOpen: true, loadingLabel: 'Clearing filters...' });
      });
    });
  }

  setFilteringState(isFiltering, loadingLabel = this.defaultLoadingLabel) {
    this.isFiltering = Boolean(isFiltering);
    this.root.classList.toggle('uc-is-filtering', this.isFiltering);

    if (this.facetsForm) {
      this.facetsForm.classList.toggle('is-loading', this.isFiltering);
      this.facetsForm.setAttribute('aria-busy', this.isFiltering ? 'true' : 'false');
      this.facetsForm.dataset.loadingLabel = this.isFiltering ? loadingLabel : this.defaultLoadingLabel;
      const controls = this.facetsForm.querySelectorAll('input, button, select');
      controls.forEach((control) => {
        control.disabled = this.isFiltering;
      });
    }

    if (this.loadMoreBtn) {
      this.loadMoreBtn.disabled = this.isFiltering;
    }
  }

  getResultsScrollTarget() {
    return this.root.querySelector('[data-product-card]')
      || this.root.querySelector('[data-unified-grid]')
      || this.root.querySelector('.uc-empty-state')
      || this.root.querySelector('.uc-grid-section');
  }

  scrollToResultsStart() {
    window.requestAnimationFrame(() => {
      const target = this.getResultsScrollTarget();
      if (!target) return;

      const headerHeightRaw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      const headerHeight = Number.parseFloat(headerHeightRaw) || 0;
      const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - 12;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'auto'
      });
    });
  }

  async applyFacetsAjax({ keepDrawerOpen, loadingLabel = this.defaultLoadingLabel }) {
    if (!this.facetsForm || this.isFiltering) return;
    const formData = new FormData(this.facetsForm);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (value !== '' && value != null) {
        params.append(key, value);
      }
    });

    const action = this.facetsForm.getAttribute('action') || window.location.pathname;
    const requestUrl = new URL(action, window.location.origin);
    requestUrl.search = params.toString();
    requestUrl.searchParams.set('section_id', this.root.dataset.sectionId);
    this.setFilteringState(true, loadingLabel);

    try {
      const response = await fetch(requestUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nextRoot = doc.querySelector('[data-unified-collection]');
      if (!nextRoot) return;

      this.root.innerHTML = nextRoot.innerHTML;
      const browserUrl = new URL(action, window.location.origin);
      browserUrl.search = params.toString();
      window.history.replaceState({}, '', `${browserUrl.pathname}${browserUrl.search}`);
      this.root.removeAttribute('data-unified-ready');
      this.root.dataset.keepFacetsOpen = keepDrawerOpen ? 'true' : 'false';
      initUnifiedCollection();
      this.scrollToResultsStart();
    } catch (error) {
      window.location.href = `${action}${params.toString() ? `?${params.toString()}` : ''}`;
    } finally {
      this.setFilteringState(false);
    }
  }

  bindLoadMore() {
    if (!this.loadMoreBtn || !this.grid) return;
    this.loadMoreBtn.addEventListener('click', async () => {
      const nextUrl = this.loadMoreBtn.dataset.nextUrl;
      if (!nextUrl) return;

      this.loadMoreBtn.disabled = true;
      this.loadMoreBtn.textContent = 'Loading...';

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.root.dataset.sectionId);
        const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nextRoot = doc.querySelector('[data-unified-collection]');
        const incomingGrid = nextRoot?.querySelector('[data-unified-grid]');
        const incomingCards = incomingGrid?.querySelectorAll('[data-product-card]') || [];

        incomingCards.forEach((card) => this.grid.appendChild(card));
        this.applyTypeFilter(this.currentType);
        document.dispatchEvent(new CustomEvent('unified:grid-updated', { detail: { grid: this.grid } }));

        const incomingNextBtn = nextRoot?.querySelector('[data-load-more]');
        if (incomingNextBtn?.dataset?.nextUrl) {
          this.loadMoreBtn.dataset.nextUrl = incomingNextBtn.dataset.nextUrl;
          this.loadMoreBtn.disabled = false;
          this.loadMoreBtn.textContent = 'Load more frames';
        } else {
          this.loadMoreBtn.remove();
        }
      } catch (error) {
        this.loadMoreBtn.disabled = false;
        this.loadMoreBtn.textContent = 'Try again';
      }
    });
  }

  bindKlarnaToggle() {
    if (!this.klarnaToggle) return;
    const defaultEnabled = this.root.dataset.defaultKlarnaOn !== 'false';
    const update = () => {
      const enabled = Boolean(this.klarnaToggle.checked);
      try {
        window.localStorage.setItem('cp_show_klarna_pricing', enabled ? '1' : '0');
      } catch (e) {}
      document.documentElement.classList.toggle('cp-klarna-on', enabled);
    };
    this.klarnaToggle.addEventListener('change', update);

    let stored = null;
    try {
      stored = window.localStorage.getItem('cp_show_klarna_pricing');
    } catch (e) {}
    let enabled = defaultEnabled;
    if (stored === '0') enabled = false;
    if (stored === '1') enabled = true;
    this.klarnaToggle.checked = enabled;
    update();
  }

  syncKlaviyoTeaserOffset() {
    const root = document.documentElement;
    const toggle = document.querySelector('[data-type-toggle-mobile]');
    if (!toggle) return;

    const style = window.getComputedStyle(toggle);
    const isDisplayed = style.display !== 'none' && style.visibility !== 'hidden';
    const isMobileViewport = window.matchMedia('(max-width: 1023px)').matches;

    if (!isDisplayed || !isMobileViewport || toggle.offsetHeight <= 0) {
      root.style.setProperty('--klaviyo-teaser-bottom', '0px');
      root.classList.remove('uc-type-toggle-mobile-active');
      return;
    }

    root.style.setProperty('--klaviyo-teaser-bottom', `${toggle.offsetHeight}px`);
    root.classList.add('uc-type-toggle-mobile-active');
  }

  bindTypeToggle() {
    if (!this.typeBtns.length || !this.grid) {
      this.syncKlaviyoTeaserOffset();
      return;
    }
    const cards = [...this.grid.querySelectorAll('[data-product-card]')];
    let hasOpticals = false;
    let hasSunglasses = false;
    cards.forEach((card) => {
      const type = card.dataset.productType;
      if (type === 'opticals') hasOpticals = true;
      if (type === 'sunglasses') hasSunglasses = true;
    });

    // If only one category exists, hide the toggle/badge UI completely.
    const typeToggleDesktop = this.root.querySelector('[data-type-toggle]');
    const typeToggleMobile = this.root.querySelector('[data-type-toggle-mobile]');
    if ((hasOpticals && !hasSunglasses) || (hasSunglasses && !hasOpticals) || (!hasOpticals && !hasSunglasses)) {
      if (typeToggleDesktop) typeToggleDesktop.style.display = 'none';
      if (typeToggleMobile) typeToggleMobile.style.display = 'none';
      this.currentType = 'all';
      this.applyTypeFilter('all');
      this.syncKlaviyoTeaserOffset();
      return;
    } else {
      if (typeToggleDesktop) typeToggleDesktop.style.display = '';
      if (typeToggleMobile) typeToggleMobile.style.display = '';
    }

    if (hasSunglasses && hasOpticals) {
      const activeBtn = this.root.querySelector('.CollectionTypeBtn.is-active');
      this.currentType = activeBtn?.dataset.typeFilter || 'all';
    } else if (hasSunglasses && !hasOpticals) {
      this.currentType = 'sunglasses';
    } else if (hasOpticals && !hasSunglasses) {
      this.currentType = 'opticals';
    } else {
      this.currentType = 'all';
    }

    this.typeBtns.forEach((btn) => {
      const shouldHide = (btn.dataset.typeFilter === 'opticals' && !hasOpticals)
        || (btn.dataset.typeFilter === 'sunglasses' && !hasSunglasses)
        || (btn.dataset.typeFilter === 'all' && !(hasOpticals && hasSunglasses));
      btn.classList.toggle('hidden-category', shouldHide);
      btn.classList.toggle('is-active', !shouldHide && btn.dataset.typeFilter === this.currentType);
      btn.addEventListener('click', () => {
        if (btn.classList.contains('hidden-category')) return;
        this.currentType = btn.dataset.typeFilter;
        this.applyTypeFilter(this.currentType);
        this.syncTypeButtons(this.currentType);
      });
    });

    this.applyTypeFilter(this.currentType);
    this.syncTypeButtons(this.currentType);
    this.syncKlaviyoTeaserOffset();
  }

  syncTypeButtons(type) {
    this.typeBtns = [...this.root.querySelectorAll('.CollectionTypeBtn')];
    this.typeBtns.forEach((btn) => {
      if (btn.classList.contains('hidden-category')) return;
      btn.classList.toggle('is-active', btn.dataset.typeFilter === type);
    });
  }

  applyTypeFilter(type) {
    if (!this.grid) return;
    const cards = [...this.grid.querySelectorAll('[data-product-card]')];
    cards.forEach((card) => {
      const isMatch = type === 'all' ? true : card.dataset.productType === type;
      card.classList.toggle('hidden-type', !isMatch);
      card.style.display = isMatch ? '' : 'none';
    });
    this.updateVisibleCount();
  }

  updateVisibleCount() {
    const count = this.grid
      ? this.grid.querySelectorAll('[data-product-card]:not(.hidden-type)').length
      : 0;
    const countElement = this.root.querySelector('.uc-toolbar__count');
    if (countElement) {
      const label = count === 1 ? 'frame' : 'frames';
      countElement.textContent = `${count} ${label}`;
    }
  }

  setFacetsOpen(isOpen) {
    if (!this.facets) return;
    this.facets.classList.toggle('is-open', Boolean(isOpen));
    this.syncScrollLock();
  }

  syncScrollLock() {
    const hasOpenFacets = Boolean(document.querySelector('.uc-facets.is-open'));
    document.documentElement.classList.toggle('uc-scroll-lock', hasOpenFacets);
  }

  bindHeroVideo() {
    const hero = this.root.querySelector('.uc-hero');
    if (!hero) return;
    const mediaWrap = hero.querySelector('.uc-hero__media-wrap');
    if (!mediaWrap) return;

    const videos = [...hero.querySelectorAll('video[data-uc-hero-video]')];
    if (!videos.length) return;

    const getToggle = (mode) => hero.querySelector(`[data-uc-hero-video-toggle="${mode}"]`);
    const getVideo = (mode) => hero.querySelector(`video[data-uc-hero-video="${mode}"]`);
    let fullscreenCloseBtn = null;
    let isPseudoFullscreen = false;

    const playIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"></path>
      </svg>`;

    const pauseIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 5h4v14H6zM14 5h4v14h-4z"></path>
      </svg>`;

    const setToggleState = (mode, isPlaying) => {
      const btn = getToggle(mode);
      if (!btn) return;
      const iconWrap = btn.querySelector('.uc-hero__video-toggle-icon');
      if (iconWrap) {
        iconWrap.dataset.state = isPlaying ? 'pause' : 'play';
        iconWrap.innerHTML = isPlaying ? pauseIcon : playIcon;
      }
      btn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      btn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    };

    const tryPlay = (videoEl) => {
      if (!videoEl) return;
      const p = videoEl.play?.();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {});
      }
    };

    const resetControls = () => {
      videos.forEach((videoNode) => {
        videoNode.controls = false;
      });
    };

    const setMediaWrapFullscreenState = (isFullscreen) => {
      mediaWrap.classList.toggle('uc-hero__media-wrap--is-fullscreen', Boolean(isFullscreen));
      if (isFullscreen) {
        if (!fullscreenCloseBtn) {
          fullscreenCloseBtn = document.createElement('button');
          fullscreenCloseBtn.type = 'button';
          fullscreenCloseBtn.className = 'uc-hero__fullscreen-close';
          fullscreenCloseBtn.setAttribute('aria-label', 'Close fullscreen video');
          fullscreenCloseBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
          fullscreenCloseBtn.addEventListener('click', onFullscreenCloseClick);
        }
        if (!fullscreenCloseBtn.isConnected) {
          mediaWrap.appendChild(fullscreenCloseBtn);
        }
      } else if (fullscreenCloseBtn) {
        fullscreenCloseBtn.remove();
      }
    };

    const setPseudoFullscreenState = (enabled) => {
      isPseudoFullscreen = Boolean(enabled);
      mediaWrap.classList.toggle('uc-hero__media-wrap--pseudo-fullscreen', isPseudoFullscreen);
      document.documentElement.classList.toggle('uc-hero-media-wrap-lock', isPseudoFullscreen);
      document.body.classList.toggle('uc-hero-media-wrap-lock', isPseudoFullscreen);
      setMediaWrapFullscreenState(isPseudoFullscreen || Boolean(document.fullscreenElement));
      if (!isPseudoFullscreen) {
        resetControls();
      }
    };

    const requestFullscreen = (videoEl) => {
      if (!videoEl) return Promise.resolve(false);
      if (typeof mediaWrap.requestFullscreen === 'function') {
        return mediaWrap.requestFullscreen().then(() => true);
      }
      if (typeof videoEl.requestFullscreen === 'function') {
        return videoEl.requestFullscreen().then(() => true);
      }
      return Promise.resolve(false);
    };

    const exitFullscreen = () => {
      if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
        return document.exitFullscreen().catch(() => {});
      }
      return Promise.resolve();
    };

    function onFullscreenCloseClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (isPseudoFullscreen) {
        setPseudoFullscreenState(false);
        return;
      }
      exitFullscreen().finally(() => {
        setMediaWrapFullscreenState(false);
        resetControls();
      });
    }

    const bindOne = (mode) => {
      const videoEl = getVideo(mode);
      if (!videoEl) return;

      // Autoplay can fail on some browsers; keep retrying on first user gesture.
      tryPlay(videoEl);
      videoEl.addEventListener('canplay', () => tryPlay(videoEl), { once: true });

      const btn = getToggle(mode);
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (videoEl.paused) {
            tryPlay(videoEl);
          } else {
            videoEl.pause();
          }
          setToggleState(mode, !videoEl.paused);
        });
      }

      videoEl.addEventListener('play', () => setToggleState(mode, true));
      videoEl.addEventListener('pause', () => setToggleState(mode, false));

      // Click video to open fullscreen with controls.
      videoEl.addEventListener('click', () => {
        requestFullscreen(videoEl)
          .then((openedNativeFullscreen) => {
            if (!openedNativeFullscreen) {
              setPseudoFullscreenState(true);
            }
            setMediaWrapFullscreenState(true);
            videoEl.controls = true;
          })
          .catch(() => {
            setPseudoFullscreenState(true);
            setMediaWrapFullscreenState(true);
            videoEl.controls = true;
          });

        tryPlay(videoEl);
      });

      const onFullscreenChange = () => {
        const fsEl = document.fullscreenElement;
        const isFullScreen = fsEl === mediaWrap || fsEl === videoEl;
        if (!isPseudoFullscreen) {
          setMediaWrapFullscreenState(isFullScreen);
        }
        if (!isFullScreen && !isPseudoFullscreen) {
          resetControls();
        }
      };

      document.addEventListener('fullscreenchange', onFullscreenChange);
      // iOS Safari emits these on the video element.
      videoEl.addEventListener('webkitbeginfullscreen', () => {
        videoEl.controls = true;
        setMediaWrapFullscreenState(true);
      });
      videoEl.addEventListener('webkitendfullscreen', () => {
        if (!document.fullscreenElement && !isPseudoFullscreen) {
          setMediaWrapFullscreenState(false);
          resetControls();
        }
      });
    };

    bindOne('desktop');
    bindOne('mobile');
  }
}

const initUnifiedCollection = () => {
  document.querySelectorAll('[data-unified-collection]:not([data-unified-ready])').forEach((root) => {
    root.dataset.unifiedReady = 'true';
    new UnifiedCollectionPage(root);
    if (window.GlobalProductCardRegistry && typeof window.GlobalProductCardRegistry.init === 'function') {
      window.GlobalProductCardRegistry.init(root);
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnifiedCollection);
} else {
  initUnifiedCollection();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-unified-collection]');
  if (!root) return;
  root.removeAttribute('data-unified-ready');
  initUnifiedCollection();
});
